import { readField } from "./viewContract";

/**
 * SHARED data-binding helpers for self-contained widgets (TableWidgetView,
 * DetailWidgetView). Both widgets use the IDENTICAL field model — a `selected` list
 * mixing plain main-collection scalars, dotted one-hop relation paths
 * `<mainShort>.<relation>.<field>`, and "documentId" — so the Strapi query construction
 * and the relation-prefix-strip value read are factored here rather than duplicated.
 */

/**
 * Build the Strapi 5 REST `fields`/`populate` query for a widget's data binding.
 *
 * `selected` is a MIX of plain main-collection scalar field names (e.g. "name") and
 * dotted one-hop relation paths `<collectionShortName>.<relation>.<subfield>`. Strapi 5
 * rejects dotted relation paths in top-level `fields[]`, so entries are classified:
 *  - RELATION FIELD PATH iff split on "." it is `<short>.<relation>.<rest>` where
 *    `<relation>` is one of `relations`; the short-name prefix is stripped, the next
 *    segment is the relation, the remainder is the sub-field.
 *  - everything else is a main-collection scalar.
 *
 * Emission: main scalars -> `fields[N]=`; relation sub-fields grouped per relation ->
 * `populate[<relation>][fields][K]=`; a relation with no selected sub-field ->
 * `populate[<relation>]=true`.
 */
export function buildFetchQuery(
  selected: string[],
  relations: string[],
  collectionShortName: string
): string {
  const prefix = collectionShortName ? `${collectionShortName}.` : "";
  const scalarFields: string[] = [];
  const relationSubFields = new Map<string, string[]>();

  for (const entry of selected) {
    const segments = entry.split(".");
    if (prefix && entry.startsWith(prefix) && segments.length >= 3) {
      const relation = segments[1];
      if (relations.includes(relation)) {
        const subField = segments.slice(2).join(".");
        const existing = relationSubFields.get(relation);
        if (existing) existing.push(subField);
        else relationSubFields.set(relation, [subField]);
        continue;
      }
    }
    scalarFields.push(entry);
  }

  const parts: string[] = [];

  scalarFields.forEach((f, i) => {
    parts.push(`fields[${i}]=${encodeURIComponent(f)}`);
  });

  for (const relation of relations) {
    const subFields = relationSubFields.get(relation);
    const rel = encodeURIComponent(relation);
    if (subFields && subFields.length > 0) {
      subFields.forEach((sf, k) => {
        parts.push(`populate[${rel}][fields][${k}]=${encodeURIComponent(sf)}`);
      });
    } else {
      parts.push(`populate[${rel}]=true`);
    }
  }

  return parts.join("&");
}

/**
 * Map a STORED field path to the path actually present on the Strapi record. A stored
 * relation path is "<mainShort>.<relation>.<field>" but the record is shaped
 * "<relation>.<field>" (no main-short prefix), so the leading main-short segment is
 * stripped. Plain (single-segment) fields are returned as-is.
 */
export function toRecordPath(field: string, collectionShortName: string): string {
  const prefix = `${collectionShortName}.`;
  return field.startsWith(prefix) ? field.slice(prefix.length) : field;
}

/**
 * Read a (possibly relation-pathed) field off a record and stringify it the SAME way
 * table cells do: null/undefined => "-", object => JSON.stringify, else String().
 */
export function readFieldString(
  record: Record<string, any>,
  field: string,
  collectionShortName: string
): string {
  const value = readField(record, toRecordPath(field, collectionShortName));
  if (value == null) return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
